import MojitoNGen from 'mojito/ngen';
import { Suspense } from 'react';
import Hooks from 'core/presentation/hooks';
import { useErrorBoundary } from 'preact/hooks';

const log = MojitoNGen.logger.get('UISuspense');

/**
 * UISuspense is a High Order Component (HoC) function used to wrap the component implementation
 * with Suspense functionality to show loading view and handle errors during lazy loadable component loading.
 *
 * @function UISuspense
 * @memberof Mojito.Core.Presentation
 *
 * @param {string} name - The name of the view.
 * @param {object} Impl - The implementation of the component.
 * @param {Function} [loadingView] - Loading view component will be shown while component is suspending.
 *
 * @returns {Function} MojitoComponent function.
 */
export default function UISuspense(name, Impl, loadingView) {
    const SuspenseWrapper = props => {
        const appContext = Hooks.useAppContext();
        const [error] = useErrorBoundary();

        if (error) {
            log.error(`Error in lazy loaded ${name}:`, error);
            const ErrorComponent = appContext.errorFallback || (
                <div className={'ta-ComponentErrorRenderer'} />
            );
            return <ErrorComponent error={error} />;
        }
        return (
            <Suspense fallback={createLoadingComponent(name, loadingView, appContext)}>
                <Impl {...props} />
            </Suspense>
        );
    };
    SuspenseWrapper.displayName = `${name}UISuspenseWrapper`;
    return SuspenseWrapper;
}

const createLoadingComponent = (name, loaderElement, appContext) => {
    const Loader = loaderElement || appContext.suspenseFallback;
    if (!Loader) {
        return null;
    }
    const acceptsName = Loader?.propTypes?.hasOwnProperty('name');
    return acceptsName ? <Loader name={name} /> : <Loader />;
};
